/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/first.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * This function calculates the earliest occurrence of the search field
 * (the first parameter), where sorting order is determined by the sort
 * field (the second parameter). This sorting order is always ascending
 * and null values always sort last. Both fields support null,
 * single-valued, and multi-valued input. If the earliest sort field
 * value appears in multiple documents, this function is allowed to
 * return any corresponding search field value.
 *
 * @example
 *         @timestamp        |  name   | number
 * "2025-11-25T00:00:00.000Z | alpha   | 1"
 * "2025-11-25T00:00:01.000Z | alpha   | 2"
 * "2025-11-25T00:00:02.000Z | bravo   | null"
 * "2025-11-25T00:00:03.000Z | alpha   | 4"
 * "2025-11-25T00:00:04.000Z | bravo   | 5"
 * "2025-11-25T00:00:05.000Z | charlie | [6, 7, 8]"
 * "2025-11-25T00:00:06.000Z | delta   | null"
 *
 * From dataset
 * | STATS first_val = FIRST(number, @timestamp)
 */
const definition: FunctionDefinition = {
  type: 'agg',
  name: 'first',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date_nanos',
    },
    {
      params: [
        {
          name: 'field',
          type: 'dense_vector',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'dense_vector',
    },
    {
      params: [
        {
          name: 'field',
          type: 'dense_vector',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'dense_vector',
    },
    {
      params: [
        {
          name: 'field',
          type: 'dense_vector',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'dense_vector',
    },
    {
      params: [
        {
          name: 'field',
          type: 'dense_vector',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'dense_vector',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'exponential_histogram',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'exponential_histogram',
    },
    {
      params: [
        {
          name: 'field',
          type: 'exponential_histogram',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'exponential_histogram',
    },
    {
      params: [
        {
          name: 'field',
          type: 'exponential_histogram',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'exponential_histogram',
    },
    {
      params: [
        {
          name: 'field',
          type: 'exponential_histogram',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'exponential_histogram',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'flattened',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_point',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohash',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohash',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geohex',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geohex',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'field',
          type: 'geotile',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'geotile',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'tdigest',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'tdigest',
    },
    {
      params: [
        {
          name: 'field',
          type: 'tdigest',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'tdigest',
    },
    {
      params: [
        {
          name: 'field',
          type: 'tdigest',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'tdigest',
    },
    {
      params: [
        {
          name: 'field',
          type: 'tdigest',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'tdigest',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'sortField',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
